import React from 'react';
import { Element } from 'react-scroll';
import { styled } from '@mui/material/styles';
import { Container, Button, Box, Typography, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DirectionsIcon from '@mui/icons-material/Directions';

import ContactsTypography from '../components/ContactsTypography';

const Section = styled('section')(({ theme }) => ({
  backgroundColor: theme.palette.primary.dark,
  padding: theme.spacing(6, 0),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(4, 0),
  },
}));

const Content = styled(Container)(({ theme }) => ({
  maxWidth: 720,
  textAlign: 'center',
  color: theme.palette.common.white,
}));

const ContactItem = styled(Box)(({ theme }) => ({
  cursor: 'pointer',
  outline: 'none',
  border: 'none',
  boxShadow: 'none',
  textDecoration: 'none',
  transition: 'color 0.2s ease, transform 0.2s ease',

  '&:hover': {
    color: theme.palette.secondary.light,
    boxShadow: 'none',
    outline: 'none',
    border: 'none',
  },

  '&:focus': {
    outline: 'none',
    boxShadow: 'none',
  },

  '&:focus-visible': {
    outline: 'none',
    boxShadow: 'none',
  },

  '& *': {
    outline: 'none',
    boxShadow: 'none',
    textDecoration: 'none',
  },
}));

function Contacts() {
  const { t } = useTranslation();

  const location = { lat: 40.31063, lng: 23.065669 };
  const phones = ['+306998469136', '+306975661980'];
  const emails = ['luxurysuites2@gmail.com', 'princess@bbqr.site'];
  const address = 'Katsirma 7, Nea Kallikratia 630 80, Chalkidiki, Greece';

  const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${location.lat},${location.lng}`;

  return (
    <Element name="contacts">
      <Section>
        <Content>
          <Typography
            variant="h3"
            component="h2"
            sx={{
              mb: { xs: 2, sm: 3 },
              color: 'secondary.light',
              textShadow: '0 2px 8px rgba(0, 0, 0, 0.45)',
              fontWeight: 700,
              fontSize: { xs: '2rem', sm: '3rem' },
            }}
          >
            {t('contacts.contacts')}
          </Typography>

          <Stack spacing={2} alignItems="center">
            {phones.map((phone) => (
              <ContactItem key={phone}>
                <ContactsTypography
                  variant="h5"
                  text={phone}
                  icon={<PhoneIcon fontSize="small" />}
                  disableHoverBorder
                  onClick={() => {
                    window.location.href = `tel:${phone}`;
                  }}
                />
              </ContactItem>
            ))}

            {emails.map((emailAddress) => (
              <ContactItem key={emailAddress}>
                <ContactsTypography
                  variant="body1"
                  text={emailAddress}
                  icon={<EmailIcon fontSize="small" />}
                  disableHoverBorder
                  onClick={() => {
                    window.location.href = `mailto:${emailAddress}`;
                  }}
                />
              </ContactItem>
            ))}

            <ContactItem>
              <ContactsTypography
                variant="body1"
                text={address}
                icon={<LocationOnIcon fontSize="small" />}
                disableHoverBorder
                onClick={() =>
                  window.open(directionsUrl, '_blank', 'noopener,noreferrer')
                }
              />
            </ContactItem>
          </Stack>

          <Button
            variant="contained"
            color="secondary"
            size="large"
            startIcon={<DirectionsIcon />}
            href={directionsUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              mt: 4,
              width: { xs: '100%', sm: 'auto' },
              maxWidth: 320,
              px: 4,
              py: 1.5,
              fontWeight: 600,
            }}
          >
            {t('contacts.getDirections')}
          </Button>
        </Content>
      </Section>
    </Element>
  );
}

export default Contacts;
